import Vue from 'vue'
import axios from 'axios'
import config from '@/config'

const superSetRoute = `${config.ENDPOINTS.NEST_BACKEND}/superset`

const dashboardTemplate = {
  namespaced: true,
  state: {
    templates: [],
    templatesCount: 0,
    templateDashboards: [],
    templateDashboardsCount: 0,
    templateDashboardsPagination: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0
    },
    enterpriseTemplates: [],
    enterpriseTemplatesCount: 0,
    dashboards: [],
    dashboardsCount: 0,
    dashboardsPagination: {
      total: 0,
      page: 1,
      limit: 10,
      totalPages: 0
    },
    categories: []
  },
  actions: {
    async fetchTemplates ({ commit }, id = null) {
      try {
        const url = id
          ? `${superSetRoute}/templates/${id}`
          : `${superSetRoute}/templates`
        const { data } = await axios.get(url)

        if (data && !data.error) {
          commit('SET_TEMPLATES', data.data || [])
          commit('SET_TEMPLATES_COUNT', (data.data || []).length)
          return data
        }
        throw new Error(data.message || 'Error al obtener templates')
      } catch (error) {
        console.error('Error fetching templates:', error)
        throw error
      }
    },
    async createTemplate ({ dispatch }, payload) {
      try {
        const url = `${superSetRoute}/templates`
        const { data } = await axios.post(url, payload)

        if (data && !data.error) {
          await dispatch('fetchTemplates')
          return data
        }
        throw new Error(data.message || 'Error al crear template')
      } catch (error) {
        console.error('Error creating template:', error)
        throw error
      }
    },
    async updateTemplate ({ dispatch }, { id, payload }) {
      try {
        const url = `${superSetRoute}/templates/${id}`
        const { data } = await axios.put(url, payload)

        if (data && !data.error) {
          await dispatch('fetchTemplates')
          return data
        }
        throw new Error(data.message || 'Error al actualizar template')
      } catch (error) {
        console.error('Error updating template:', error)
        throw error
      }
    },
    async deleteTemplate ({ dispatch }, id) {
      try {
        const url = `${superSetRoute}/templates/${id}`
        const { data } = await axios.delete(url)

        if (data && !data.error) {
          await dispatch('fetchTemplates')
          return data
        }
        return data
      } catch (error) {
        console.error('Error deleting template:', error)
        throw error
      }
    },

    // Template Dashboards Actions
    async fetchTemplateDashboards ({ commit }, { id = null, page = 1, limit = 10 } = {}) {
      try {
        let url = `${superSetRoute}/template-dashboards`
        const params = new URLSearchParams()

        if (id) {
          params.append('page', page)
          params.append('limit', limit)
          url = `${superSetRoute}/template-dashboards/${id}?${params.toString()}`
        } else {
          params.append('page', page)
          params.append('limit', limit)
          url = `${url}?${params.toString()}`
        }
        const { data } = await axios.get(url)

        if (data && !data.error) {
          commit('SET_TEMPLATE_DASHBOARDS', data.data || [])
          commit('SET_TEMPLATE_DASHBOARDS_COUNT', data.total || (data.data || []).length)
          commit('SET_TEMPLATE_DASHBOARDS_PAGINATION', {
            total: data.total || 0,
            page: data.page || 1,
            limit: data.limit || 10,
            totalPages: data.totalPages || 0
          })
          return data
        }
        throw new Error(data.message || 'Error al obtener template dashboards')
      } catch (error) {
        console.error('Error fetching template dashboards:', error)
        throw error
      }
    },
    async createTemplateDashboard ({ dispatch }, payload) {
      try {
        const url = `${superSetRoute}/template-dashboards`
        const { data } = await axios.post(url, payload)

        if (data && !data.error) {
          await dispatch('fetchTemplateDashboards')
          return data
        }
        throw new Error(data.message || 'Error al crear template dashboard')
      } catch (error) {
        console.error('Error creating template dashboard:', error)
        throw error
      }
    },
    async updateTemplateDashboard ({ dispatch }, { id, payload }) {
      try {
        const url = `${superSetRoute}/template-dashboards/${id}`
        const { data } = await axios.put(url, payload)

        if (data && !data.error) {
          await dispatch('fetchTemplateDashboards')
          return data
        }
        throw new Error(data.message || 'Error al actualizar template dashboard')
      } catch (error) {
        console.error('Error updating template dashboard:', error)
        throw error
      }
    },
    async deleteTemplateDashboard ({ dispatch }, id) {
      try {
        const url = `${superSetRoute}/template-dashboards/${id}`
        const { data } = await axios.delete(url)

        if (data && !data.error) {
          await dispatch('fetchTemplateDashboards')
          return data
        }
        throw new Error(data.message || 'Error al eliminar template dashboard')
      } catch (error) {
        console.error('Error deleting template dashboard:', error)
        throw error
      }
    },

    // Enterprise Templates Actions
    async fetchEnterpriseTemplates ({ commit }, id = null) {
      try {
        const url = id
          ? `${superSetRoute}/enterprise-templates/${id}`
          : `${superSetRoute}/enterprise-templates`
        const { data } = await axios.get(url)

        if (data && !data.error) {
          commit('SET_ENTERPRISE_TEMPLATES', data.data || [])
          commit('SET_ENTERPRISE_TEMPLATES_COUNT', (data.data || []).length)
          return data
        }
        throw new Error(data.message || 'Error al obtener enterprise templates')
      } catch (error) {
        console.error('Error fetching enterprise templates:', error)
        throw error
      }
    },
    async createEnterpriseTemplate ({ dispatch }, payload) {
      try {
        const url = `${superSetRoute}/enterprise-templates`
        const { data } = await axios.post(url, payload)

        if (data && !data.error) {
          await dispatch('fetchEnterpriseTemplates')
          return data
        }
        throw new Error(data.message || 'Error al crear enterprise template')
      } catch (error) {
        console.error('Error creating enterprise template:', error)
        throw error
      }
    },
    async updateEnterpriseTemplate ({ dispatch }, { id, payload }) {
      try {
        const url = `${superSetRoute}/enterprise-templates/${id}`
        const { data } = await axios.put(url, payload)

        if (data && !data.error) {
          await dispatch('fetchEnterpriseTemplates')
          return data
        }
        throw new Error(data.message || 'Error al actualizar enterprise template')
      } catch (error) {
        console.error('Error updating enterprise template:', error)
        throw error
      }
    },
    async deleteEnterpriseTemplate ({ dispatch }, id) {
      try {
        const url = `${superSetRoute}/enterprise-templates/${id}`
        const { data } = await axios.delete(url)

        if (data && !data.error) {
          await dispatch('fetchEnterpriseTemplates')
          return data
        }
        throw new Error(data.message || 'Error al eliminar enterprise template')
      } catch (error) {
        console.error('Error deleting enterprise template:', error)
        throw error
      }
    },

    // Dashboards Actions
    async fetchDashboards ({ commit }, { id = null, page = 1, limit = 10 } = {}) {
      try {
        let url = `${superSetRoute}/dashboards`
        const params = new URLSearchParams()

        if (id) {
          params.append('page', page)
          params.append('limit', limit)
          url = `${superSetRoute}/dashboards/${id}?${params.toString()}`
        } else {
          params.append('page', page)
          params.append('limit', limit)
          url = `${url}?${params.toString()}`
        }
        const { data } = await axios.get(url)

        if (data && !data.error) {
          commit('SET_DASHBOARDS', data.data || [])
          commit('SET_DASHBOARDS_COUNT', data.total || (data.data || []).length)
          commit('SET_DASHBOARDS_PAGINATION', {
            total: data.total || 0,
            page: data.page || 1,
            limit: data.limit || 10,
            totalPages: data.totalPages || 0
          })
          return data
        }
        throw new Error(data.message || 'Error al obtener dashboards')
      } catch (error) {
        console.error('Error fetching dashboards:', error)
        throw error
      }
    },
    async createDashboard ({ dispatch }, payload) {
      try {
        const url = `${superSetRoute}/dashboards`
        const { data } = await axios.post(url, payload)

        if (data && !data.error) {
          await dispatch('fetchDashboards')
          return data
        }
        throw new Error(data.message || 'Error al crear dashboard')
      } catch (error) {
        console.error('Error creating dashboard:', error)
        throw error
      }
    },
    async updateDashboard ({ dispatch }, { id, payload }) {
      try {
        const url = `${superSetRoute}/dashboards/${id}`
        const { data } = await axios.put(url, payload)

        if (data && !data.error) {
          await dispatch('fetchDashboards')
          return data
        }
        throw new Error(data.message || 'Error al actualizar dashboard')
      } catch (error) {
        console.error('Error updating dashboard:', error)
        throw error
      }
    },
    async deleteDashboard ({ dispatch }, id) {
      try {
        const url = `${superSetRoute}/dashboards/${id}`
        const { data } = await axios.delete(url)

        if (data && !data.error) {
          await dispatch('fetchDashboards')
          return data
        }
        throw new Error(data.message || 'Error al eliminar dashboard')
      } catch (error) {
        console.error('Error deleting dashboard:', error)
        throw error
      }
    },

    // Search Company Enterprise
    async searchCompanyEnterprise (_, search) {
      try {
        const url = `${superSetRoute}/company-enterprise/${encodeURIComponent(search)}`
        const { data } = await axios.get(url)

        if (data && !data.error) {
          return data.data || []
        }
        throw new Error(data.message || 'Error al buscar empresa')
      } catch (error) {
        console.error('Error searching company enterprise:', error)
        throw error
      }
    },

    // Categories Actions
    async fetchCategories ({ commit }, id = null) {
      try {
        const url = id
          ? `${superSetRoute}/categories/${id}`
          : `${superSetRoute}/categories`
        const { data } = await axios.get(url)

        if (data && !data.error) {
          commit('SET_CATEGORIES', data.data || [])
          return data
        }
        throw new Error(data.message || 'Error al obtener categorias')
      } catch (error) {
        console.error('Error fetching categories:', error)
        throw error
      }
    }
  },
  mutations: {
    SET_TEMPLATES (state, data) {
      Vue.set(state, 'templates', data)
    },
    SET_TEMPLATES_COUNT (state, data) {
      Vue.set(state, 'templatesCount', data)
    },
    SET_TEMPLATE_DASHBOARDS (state, data) {
      Vue.set(state, 'templateDashboards', data)
    },
    SET_TEMPLATE_DASHBOARDS_COUNT (state, data) {
      Vue.set(state, 'templateDashboardsCount', data)
    },
    SET_TEMPLATE_DASHBOARDS_PAGINATION (state, data) {
      Vue.set(state, 'templateDashboardsPagination', data)
    },
    SET_ENTERPRISE_TEMPLATES (state, data) {
      Vue.set(state, 'enterpriseTemplates', data)
    },
    SET_ENTERPRISE_TEMPLATES_COUNT (state, data) {
      Vue.set(state, 'enterpriseTemplatesCount', data)
    },
    SET_DASHBOARDS (state, data) {
      Vue.set(state, 'dashboards', data)
    },
    SET_DASHBOARDS_COUNT (state, data) {
      Vue.set(state, 'dashboardsCount', data)
    },
    SET_DASHBOARDS_PAGINATION (state, data) {
      Vue.set(state, 'dashboardsPagination', data)
    },
    SET_CATEGORIES (state, data) {
      Vue.set(state, 'categories', data)
    }
  },
  getters: {
    templatesInfo: state => state.templates,
    templatesCountInfo: state => state.templatesCount,
    templateDashboardsInfo: state => state.templateDashboards,
    templateDashboardsCountInfo: state => state.templateDashboardsCount,
    templateDashboardsPaginationInfo: state => state.templateDashboardsPagination,
    enterpriseTemplatesInfo: state => state.enterpriseTemplates,
    enterpriseTemplatesCountInfo: state => state.enterpriseTemplatesCount,
    dashboardsInfo: state => state.dashboards,
    dashboardsCountInfo: state => state.dashboardsCount,
    dashboardsPaginationInfo: state => state.dashboardsPagination,
    categoriesInfo: state => state.categories
  }
}

export default dashboardTemplate



// WEBPACK FOOTER //
// ./src/store/modules/dashboardTemplate.js